import React, { useState } from 'react'
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    IconButton,
    Menu,
    MenuItem,
    Select,
    FormControl,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Divider,
    useTheme,
    useMediaQuery,
    Avatar,
    Stack,
    Tooltip
} from '@mui/material'
import {
    Menu as MenuIcon,
    Search as SearchIcon,
    Add as AddIcon,
    Help as HelpIcon,
    AccountCircle as AccountIcon,
    Info as InfoIcon,
    ContactMail as ContactIcon,
    PrivacyTip as PrivacyIcon,
    Description as TermsIcon,
    Logout as LogoutIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

interface NavigationProps {
    currentLanguage: string
    onLanguageChange: (lang: string) => void
    themeMode: 'light' | 'dark'
    onToggleTheme: () => void
}

const Navigation: React.FC<NavigationProps> = ({ currentLanguage, onLanguageChange, themeMode, onToggleTheme }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const { authenticated, user, logout } = useAuth()

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)

    const handleLanguageChange = (lang: string) => {
        onLanguageChange(lang)
    }

    const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setUserMenuAnchor(event.currentTarget)
    }

    const handleUserMenuClose = () => {
        setUserMenuAnchor(null)
    }

    const navigationItems = React.useMemo(() => {
        const items = [
            { key: 'search', label: t('nav.search'), icon: <SearchIcon />, path: '/' },
            { key: 'offer', label: t('nav.offer'), icon: <AddIcon />, path: '/new' },
            { key: 'tips', label: t('nav.tips'), icon: <HelpIcon />, path: '/tips' }
        ]
        if (authenticated) {
            items.splice(1, 0, { key: 'myListings', label: t('nav.myListings'), icon: <AccountIcon />, path: '/my-listings' })
        }
        return items
    }, [authenticated, t])

    const footerItems = [
        { key: 'about', label: t('footer.about'), icon: <InfoIcon />, path: '/about' },
        { key: 'help', label: t('footer.help'), icon: <HelpIcon />, path: '/help' },
        { key: 'contact', label: t('footer.contact'), icon: <ContactIcon />, path: '/contact' },
        { key: 'privacy', label: t('footer.privacy'), icon: <PrivacyIcon />, path: '/privacy' },
        { key: 'terms', label: t('footer.terms'), icon: <TermsIcon />, path: '/terms' }
    ]

    const languages = [
        { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
        { code: 'es', name: 'Español', flag: '🇪🇸' }
    ]

    // const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0]

    const themeToggleLabel = themeMode === 'dark' ? t('nav.lightMode') : t('nav.darkMode')

    const MobileMenu = () => (
        <Drawer
            anchor="left"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            sx={{ '& .MuiDrawer-paper': { width: 280 } }}
        >
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {t('nav.menu')}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <List>
                    {navigationItems.map((item) => (
                        <ListItemButton
                            key={item.key}
                            onClick={() => {
                                navigate(item.path)
                                setMobileMenuOpen(false)
                            }}
                        >
                            <ListItemIcon>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.label} />
                        </ListItemButton>
                    ))}
                </List>

                <Divider sx={{ my: 2 }} />

                {!authenticated ? (
                    <List>
                        <ListItemButton
                            onClick={() => {
                                setMobileMenuOpen(false)
                                navigate(`/login?redirect=${encodeURIComponent('/new')}`)
                            }}
                        >
                            <ListItemIcon>
                                <AccountIcon />
                            </ListItemIcon>
                            <ListItemText primary={t('nav.login')} />
                        </ListItemButton>
                        <ListItemButton
                            onClick={() => {
                                setMobileMenuOpen(false)
                                navigate(`/register?redirect=${encodeURIComponent('/new')}`)
                            }}
                        >
                            <ListItemIcon>
                                <AddIcon />
                            </ListItemIcon>
                            <ListItemText primary={t('nav.register')} />
                        </ListItemButton>
                    </List>
                ) : (
                    <>
                        <Box sx={{ px: 2, pb: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">
                                {t('nav.welcome', { name: user?.name || user?.username || '' })}
                            </Typography>
                        </Box>
                        <List>
                            <ListItemButton
                                onClick={() => {
                                    navigate('/new')
                                    setMobileMenuOpen(false)
                                }}
                            >
                                <ListItemIcon>
                                    <AddIcon />
                                </ListItemIcon>
                                <ListItemText primary={t('nav.offer')} />
                            </ListItemButton>
                            <ListItemButton
                                onClick={() => {
                                    setMobileMenuOpen(false)
                                    void logout().finally(() => navigate('/'))
                                }}
                            >
                                <ListItemIcon>
                                    <LogoutIcon />
                                </ListItemIcon>
                                <ListItemText primary={t('nav.logout')} />
                            </ListItemButton>
                        </List>
                    </>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
                    {t('footer.about')}
                </Typography>

                <List>
                    {footerItems.map((item) => (
                        <ListItemButton
                            key={item.key}
                            onClick={() => {
                                navigate(item.path)
                                setMobileMenuOpen(false)
                            }}
                        >
                            <ListItemIcon>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.label} />
                        </ListItemButton>
                    ))}
                </List>

                <Divider sx={{ my: 2 }} />

                <List>
                    <ListItemButton
                        onClick={() => {
                            onToggleTheme()
                            setMobileMenuOpen(false)
                        }}
                    >
                        <ListItemIcon>
                            {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                        </ListItemIcon>
                        <ListItemText primary={themeToggleLabel} />
                    </ListItemButton>
                </List>
            </Box>
        </Drawer>
    )

    return (
        <>
            <AppBar position="static" elevation={0} sx={{ bgcolor: 'primary.main' }}>
                <Toolbar>
                    {/* Mobile Menu Button */}
                    {isMobile && (
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => setMobileMenuOpen(true)}
                            sx={{ mr: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}

                    {/* Logo */}
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{
                            flexGrow: isMobile ? 1 : 0,
                            mr: isMobile ? 0 : 4,
                            cursor: 'pointer',
                            fontWeight: 'bold'
                        }}
                        onClick={() => navigate('/')}
                    >
                        {t('header.title')}
                    </Typography>

                    {/* Desktop Navigation */}
                    {!isMobile && (
                        <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                            {navigationItems
                                .filter(item => item.key !== 'offer')
                                .map((item) => (
                                    <Button
                                        key={item.key}
                                        color="inherit"
                                        startIcon={item.icon}
                                        onClick={() => navigate(item.path)}
                                        sx={{
                                            textTransform: 'none',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                                        }}
                                    >
                                        {item.label}
                                    </Button>
                                ))}
                        </Box>
                    )}

                    {!isMobile && (
                        <Button
                            color="secondary"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate(authenticated ? '/new' : `/login?redirect=${encodeURIComponent('/new')}`)}
                            sx={{ mr: 2, textTransform: 'none' }}
                        >
                            {t('nav.offer')}
                        </Button>
                    )}

                    {/* Language Selector */}
                    <Tooltip title={themeToggleLabel}>
                        <IconButton
                            color="inherit"
                            onClick={onToggleTheme}
                            sx={{ mr: 1 }}
                            aria-label={themeToggleLabel}
                        >
                            {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                        </IconButton>
                    </Tooltip>

                    <FormControl size="small" sx={{ minWidth: 80, mr: 2 }}>
                        <Select
                            value={currentLanguage}
                            onChange={(e) => handleLanguageChange(e.target.value)}
                            variant="outlined"
                            sx={{
                                color: 'inherit',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255,255,255,0.3)',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255,255,255,0.5)',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'white',
                                },
                                '& .MuiSvgIcon-root': {
                                    color: 'white',
                                }
                            }}
                            inputProps={{ 'aria-label': 'Language' }}
                        >
                            {languages.map((lang) => (
                                <MenuItem key={lang.code} value={lang.code}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <span>{lang.flag}</span>
                                        <span>{lang.code.toUpperCase()}</span>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* User Menu */}
                    {!authenticated ? (
                        <Stack direction="row" spacing={1}>
                            <Button color="inherit" onClick={() => navigate(`/login?redirect=${encodeURIComponent('/new')}`)} sx={{ textTransform: 'none' }}>
                                {t('nav.login')}
                            </Button>
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={() => navigate(`/register?redirect=${encodeURIComponent('/new')}`)}
                                sx={{ textTransform: 'none', borderColor: 'rgba(255,255,255,0.5)' }}
                            >
                                {t('nav.register')}
                            </Button>
                        </Stack>
                    ) : (
                        <>
                            <Tooltip title={user?.name || user?.username || t('nav.myAccount')}>
                                <IconButton
                                    color="inherit"
                                    onClick={handleUserMenuOpen}
                                    sx={{
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                                    }}
                                >
                                    <Avatar sx={{ width: 32, height: 32 }}>
                                        {(user?.name || user?.username || '?').substring(0, 1).toUpperCase()}
                                    </Avatar>
                                </IconButton>
                            </Tooltip>

                            <Menu
                                anchorEl={userMenuAnchor}
                                open={Boolean(userMenuAnchor)}
                                onClose={handleUserMenuClose}
                            >
                                <MenuItem disabled>{user?.name || user?.username}</MenuItem>
                                <MenuItem onClick={() => { navigate('/my-listings'); handleUserMenuClose(); }}>
                                    {t('nav.myListings')}
                                </MenuItem>
                                <MenuItem onClick={() => { navigate('/new'); handleUserMenuClose(); }}>
                                    {t('nav.offer')}
                                </MenuItem>
                                <MenuItem onClick={() => { handleUserMenuClose(); void logout().finally(() => navigate('/')) }}>
                                    {t('nav.logout')}
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                </Toolbar>
            </AppBar>

            <MobileMenu />
        </>
    )
}

export default Navigation
